import { useParams, useSearchParams } from "@solidjs/router";
import { stAnalytics } from "@storytell/analytics";
import { useAuthWithSSRService } from "@storytell/auth-ssr-implementation";
import {
  clearLastProjectCookie,
  lastProjectCookie,
  setLastProjectCookie,
} from "@storytell/auth-ssr-implementation/src/redirectHintCookies";
import { configureStorytellClient } from "@storytell/client";
import {
  createActiveContextStore,
  createAssetsService,
  createBillingService,
  createChatService,
  createConceptsService,
  createConnectivityService,
  createEventsService,
  createFeatureGateService,
  createGrantsService,
  createKnowledgeUIService,
  createLabelsService,
  createNavigationService,
  createNotificationsService,
  createProjectsService,
  createRelsService,
  createStoredPromptsService,
  createSuggestionsService,
  createTenantsStore,
  createUIService,
  createUserContextService,
  createUserStore,
  createViewsService,
} from "@storytell/core";
import {
  configureCpClient,
  createListRecentChatsRequest,
  useCallOptions,
  useChatClient,
  withBearerToken,
} from "@storytell/cp-client";
import { useDeviceService } from "@storytell/device";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import { useLocaleService } from "@storytell/locale";
import { Client as ClientLogger, type Logger, Server as ServerLogger } from "@storytell/logger";
import { createStreamingStore } from "@storytell/streaming";
import { type ProjectId, ROLE_OWNER } from "@storytell/types";
import { createEffect, createMemo, createSignal, on, onCleanup, onMount, startTransition } from "solid-js";
import { getRequestEvent, isServer } from "solid-js/web";
import { useSafeI18n } from "~/i18n/context";
import { trackFeature } from "~/lib/trackFeature";
import type { Wire } from "./definition";
import {
  isChatStreamFeatureGatePermissionError,
  isChatStreamInsufficientCreditsError,
} from "./isChatStreamFeatureGateError";
import { createActionsService } from "./services/ActionsService";
import { createArtifactsService } from "./services/ArtifactsService";
import { ChatTitleService } from "./services/chatTitleService";
import { createDevicesService } from "./services/DevicesService";
import { createLiveSessionsService } from "./services/LiveSessionsService";
import { createMemoryService } from "./services/MemoryService";
import { createProjectBillingService } from "./services/ProjectBillingService";
import { createRecentChatsService } from "./services/RecentChatsService";
import { createSkillsService } from "./services/SkillsService";
import { createTokensService } from "./services/tokens";

declare global {
  interface Window {
    // Set by newWire() in dev builds only, for console inspection.
    stWire?: Wire;
  }
}

// Placeholder for the late-bound callbacks in newWire, replaced once the rels service exists.
const noRelsYet = () => {
  /* no-op */
};

export const newWire = (): Wire => {
  const logger = initLogger();
  const locale = useLocaleService();
  // The auth layer reports this locale to Firebase and to the user sync, so it
  // has to follow the language the app is rendering in rather than sit on its
  // default. Seeded eagerly because effects do not run during SSR, and read
  // through the safe accessor so a tree without the provider keeps the default.
  const { currentLanguage, t } = useSafeI18n();
  locale.setLocale(currentLanguage());
  createEffect(() => locale.setLocale(currentLanguage()));
  const device = useDeviceService({
    variant: "app",
  });
  const event = getRequestEvent();

  const streamingService = createStreamingStore();

  const userAgent = () => {
    if (isServer) {
      return event?.request?.headers.get("user-agent") || "";
    }
    return navigator.userAgent;
  };

  const auth = useAuthWithSSRService({
    logger,
    deviceId: device.getDeviceId,
    locale: locale.getLocale,
    userAgent,
  });

  const knowledgeUi = createKnowledgeUIService();

  const routeParams = useParams();
  const [searchParams] = useSearchParams<{ side?: string }>();
  // Declared early so guards (e.g. fetchHasChats) can gate RPCs that require X-Project-Id.
  const urlProjectId = () => routeParams.projectId as ProjectId | undefined;
  const urlOrgId = () => routeParams.orgId;

  // TODO: @andi Better way to check if the guest user has started any chats
  const [hasChats, setHasChats] = createSignal(false);
  const fetchHasChats = async () => {
    if (!auth.isReady() || !auth.isGuest() || !auth.userId() || !urlProjectId()) return;
    const client = useChatClient();
    try {
      const res = await client.listRecentChats({ pageSize: 1 }, useCallOptions(withBearerToken(auth.token())));
      setHasChats(res.isOk() ? Number(res.value.totalCount) > 0 : false);
    } catch (error) {
      console.error("Failed to check for guest chats:", error);
    }
  };
  createEffect(
    on(
      () => auth.isReady() && auth.isGuest() && urlProjectId(),
      () => {
        void fetchHasChats();
      }
    )
  );
  onMount(() => {
    const unsubscribe = subscribeToGlobalEvent("prompt-submitted-successfuly", () => {
      void fetchHasChats();
    });
    onCleanup(unsubscribe);
  });

  const ui = createUIService();

  const chatTitle = new ChatTitleService();

  /**
   * Creates a params object that includes chatId derived from the new URL format.
   * Checks both primary content and side panel for chat IDs.
   *
   * URL formats supported:
   * - Old: /project/:projectId/chat/:chatId
   * - New primary: /project/:projectId/:primaryId (where primaryId can be chat_xxx)
   * - New side: /project/:projectId/:primaryId?side=chat_xxx
   * - Execution: /project/:projectId/actions/:actionId/executions/:chatId
   *
   * Priority: old format > execution route > primary chat > side panel chat
   */
  const getChatParams = () => {
    if (routeParams.chatId) {
      return routeParams;
    }
    if (routeParams.primaryId?.startsWith("chat_")) {
      return { ...routeParams, chatId: routeParams.primaryId };
    }
    if (searchParams.side?.startsWith("chat_")) {
      return { ...routeParams, chatId: searchParams.side };
    }
    return routeParams;
  };

  const projects = createProjectsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
  });

  const user = createUserStore({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
  });

  const tenants = createTenantsStore({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
  });

  const activeContext = createActiveContextStore({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    projectId: urlProjectId,
    orgId: urlOrgId,
  });

  const userContext = createUserContextService({
    activeContext,
    tenants,
  });

  // URL wins; activeContext fills in on routes that don't express the scope in
  // their path (/orgs/{orgId}/..., /settings, /). The backend /context response
  // seeds projectId from the user's server-persisted last-focused project.
  const currentOrgId = () => urlOrgId() ?? activeContext.activeContext()?.organizationId;
  // On the org route /context auto-selects a default project inside the viewed
  // org, not where the user came from. Prefer their last real project so "home"
  // returns there.
  const currentProjectId = () =>
    urlProjectId() ??
    (urlOrgId()
      ? (userContext.getRecentProjectIds()[0] as ProjectId | undefined)
      : activeContext.activeContext()?.projectId);

  // currentOrgId() can resolve to a project's owning org the user only holds
  // project-scope access in. /orgs/{id} is owner-only, so using it as a
  // home/fallback redirect target lands a non-owner on the access-denied
  // screen (ENG-6478). Gate it against the /tenants grant list and only treat
  // an org as a safe home target when the user owns it at org scope.
  const homeOrgId = (): string | undefined => {
    const orgId = currentOrgId();
    if (!orgId) return undefined;
    const ownsOrg = tenants
      .tenants()
      .some((tenant) => tenant.organizationId === orgId && tenant.organizationRole === ROLE_OWNER);
    return ownsOrg ? orgId : undefined;
  };

  // Groups need PostHog's Group Analytics add-on, so the ids also ride every event as super
  // properties. Guests stay ungrouped: a group turns an anonymous visitor into a person profile.
  // Sign-out's reset clears both. Memoized because every active-context refetch hands back a new object
  // with the same ids, and posthog.group rewrites its localStorage blob even for an unchanged key.
  const analyticsOrgId = createMemo(currentOrgId);
  const analyticsProjectId = createMemo(currentProjectId);
  createEffect(() => {
    if (isServer) return;
    const orgId = analyticsOrgId();
    if (!orgId) return;
    stAnalytics.plugins.posthog.register({ organization_id: orgId });
    if (auth.isRegistered()) stAnalytics.plugins.posthog.group("organization", orgId);
  });
  createEffect(() => {
    if (isServer) return;
    const projectId = analyticsProjectId();
    if (!projectId) return;
    stAnalytics.plugins.posthog.register({ project_id: projectId });
    if (auth.isRegistered()) stAnalytics.plugins.posthog.group("project", projectId);
  });

  // Latch the active project into recents so scope-less routes (/upgrade,
  // /settings) and the Stripe full-page round-trip can recover which project
  // the user was in — the URL and /context both drop the project there.
  createEffect(() => {
    const projectId = urlProjectId();
    if (projectId) userContext.recordRecentProject(projectId);
  });

  // Mirror the open project into a cookie so the middleware can send a returning
  // user from / straight into it. Only a project present in the user's own
  // project list is written, and the cookie is dropped the moment that list
  // contradicts it, so a second account signing in on the same browser can't
  // inherit the first one's destination.
  createEffect(() => {
    if (isServer || !auth.isReady()) return;
    if (!auth.isRegistered()) {
      clearLastProjectCookie();
      return;
    }
    const projectId = urlProjectId();
    if (!projectId) return;
    if (projects.get(projectId)) {
      setLastProjectCookie(projectId);
      return;
    }
    // A failed /projects-sync is indistinguishable from an empty one, so only a
    // list that came back with something counts as evidence against the URL.
    if (projects.all().length > 0) clearLastProjectCookie();
  });

  // The list check above can't see a project deleted or revoked since the cookie
  // was written when the sync itself is failing; the backend denial can. Recents
  // are dropped on the same signal: they are the other half of "where was I
  // last", read as a destination by the settings back-link and by the home
  // fallback above on the /orgs routes, which would otherwise keep steering back
  // into the denial. The scope-less routes (/upgrade, the usage panel) read the
  // retained context ahead of recents and are not covered by this.
  //
  // The denial also fires on 401 and 404, which a stale token or a race can
  // produce for a project the user still holds, and the latch above won't
  // re-record while the user stays on the route. So a project dropped here goes
  // back the moment the backend answers for it again.
  if (!isServer) {
    const forgottenProjectIds = new Set<string>();
    onMount(() => {
      const unsubscribeDenied = subscribeToGlobalEvent("project-access-denied", (deniedEvent) => {
        const deniedProjectId = deniedEvent.detail?.projectId;
        if (!deniedProjectId) return;
        if (deniedProjectId === lastProjectCookie(document.cookie)) clearLastProjectCookie();
        forgottenProjectIds.add(deniedProjectId);
        userContext.forgetRecentProject(deniedProjectId);
      });
      const unsubscribeOk = subscribeToGlobalEvent("project-access-ok", (okEvent) => {
        const okProjectId = okEvent.detail?.projectId;
        if (!okProjectId || !forgottenProjectIds.delete(okProjectId)) return;
        userContext.recordRecentProject(okProjectId as ProjectId);
      });
      onCleanup(() => {
        unsubscribeDenied();
        unsubscribeOk();
      });
    });
  }

  const primaryTenant = () => tenants.tenants().find((tenant) => tenant.isPrimary) ?? tenants.tenants()[0];

  const currentTenant = () => {
    const orgId = currentOrgId();
    if (orgId) {
      const match = tenants.tenants().find((tenant) => tenant.organizationId === orgId);
      if (match) return match;
    }
    return primaryTenant();
  };

  const billing = createBillingService({
    authReady: auth.isReady,
    authToken: auth.token,
    authUserId: auth.userId,
    authRegistered: () => auth.isRegistered(),
    hasChats,
    userId: auth.userId,
    // The plan and balance on an org page belong to the org in the URL, not to
    // whichever one the user last worked in.
    organizationId: () => currentTenant()?.organizationId,
  });

  // Every answer spends from this balance and the credit gates decide on it, so it refetches with
  // the home balance in reloadBillingInfo.
  const projectBilling = createProjectBillingService({
    authReady: () => auth.isReady(),
    authGuest: () => auth.isGuest(),
    authToken: () => auth.token(),
    projectId: currentProjectId,
  });

  const notifications = createNotificationsService({
    authReady: auth.isReady,
    authToken: auth.token,
    authUserId: auth.userId,
  });

  const featureGate = createFeatureGateService({
    // Entitlements ride per-tenant on the /tenants response.
    entitlements: () => currentTenant()?.entitlements,
  });

  // Header stamping is URL-strict: stamp X-Project-Id / X-Organization-Id only
  // when the URL expresses that scope. No fallback to last-focused — that
  // prevents scope-leak (an org-page request getting back project-scope data
  // because we silently stamped X-Project-Id from a stale signal). The
  // fallback in wire.currentProjectId / wire.currentOrgId is for URL
  // construction (nav links, home button), not the API contract.
  configureStorytellClient({
    getProjectId: urlProjectId,
    getOrgId: urlOrgId,
  });

  configureCpClient({
    getProjectId: urlProjectId,
  });

  let assetsOrConceptsSynced = noRelsYet;
  const assets = createAssetsService({
    authToken: () => auth.token(),
    projectId: urlProjectId,
    assetsSynced: () => assetsOrConceptsSynced(),
  });

  const chat = createChatService({
    urlParams: getChatParams,
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    projectId: urlProjectId,
    workspaceId: () => activeContext.activeContext()?.workspaceId,
    stream: streamingService,
    assets,
    reloadBillingInfo: () => {
      // In a transition so the refetch doesn't suspend the global modals' boundary — the
      // out-of-credits modal opens right after this and would otherwise wait on the request.
      void startTransition(() => {
        void billing.refetch();
        projectBilling.refetch();
      });
    },
    setChatTitle: (chatId, title) => {
      chatTitle.setChatTitle(chatId, title);
    },
    onChatStreamFailed: (error, { mode, resubmitWithAutoMode, chatNamed }) => {
      const errorMsg = error instanceof Error ? error.message : String(error);
      dispatchGlobalEvent("prompt-submission-failed", { reason: errorMsg });
      if (errorMsg.includes("no_available_model") || errorMsg.includes("provider is temporarily unavailable")) {
        dispatchGlobalEvent("vendor-unavailable-error", { originalPromptText: "" });
        return;
      }
      // The pre-submit credit gate reads a client-side balance that can lag the server's, so the
      // server's refusal is the one that has to reach the user.
      if (isChatStreamInsufficientCreditsError(error)) {
        ui.creditsGate.openModal(t("modal_insufficient_credits_title_personal"));
        return;
      }
      if (isChatStreamFeatureGatePermissionError(mode, error)) {
        ui.disallowed.openModal({ resubmitWithAutoMode });
        return;
      }
      // The prompt bar reports a refused attachment next to the chips it drops.
      if (errorMsg.includes("chats.send.handler.invalid_attachment")) return;
      // A refusal before the server names the chat leaves nothing in the thread, and the composer
      // still holds the prompt, so without this the submit looks like it did nothing. After that
      // the turn is on screen, and a transient drop there may still recover.
      if (!chatNamed) dispatchGlobalEvent("chat-turn-failed", {});
    },
  });

  const views = createViewsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
    projectId: urlProjectId,
  });

  const concepts = createConceptsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
    projectId: urlProjectId,
    conceptsSynced: () => assetsOrConceptsSynced(),
  });
  let rebuildRelsFromCurrentEntities = noRelsYet;
  const labels = createLabelsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
    projectId: urlProjectId,
    onAssociationsChanged: () => rebuildRelsFromCurrentEntities(),
  });
  const storedPrompts = createStoredPromptsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    userId: auth.userId,
    projectId: urlProjectId,
  });
  const navigation = createNavigationService({
    ui,
    currentProjectId,
    homeOrgId,
    onSidePanelAction: (action) => trackFeature({ feature: "nav.side_panel", action, via: "button" }),
  });
  const rels = createRelsService({
    assets,
    concepts,
    latestLabelAssociations: () => labels.getLatestLabelAssociations(),
  });
  rebuildRelsFromCurrentEntities = () => {
    rels.rebuildFullCache({ assets: assets.all(), concepts: concepts.all() });
  };

  // Have to define this later so we can access the assets and concepts services
  assetsOrConceptsSynced = () => {
    rels.rebuildFullCache({ assets: assets.all(), concepts: concepts.all() });
  };

  const grants = createGrantsService({
    activeContext,
  });

  // Constructed for its SSE subscription: it dispatches "server-event-received"
  // globally, which the assets, concepts, labels, views and stored-prompts
  // services subscribe to. Nothing calls back into it, so it is not on the wire.
  createEventsService({
    projectId: urlProjectId,
    authToken: () => auth.token(),
  });

  const suggestions = createSuggestionsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    projectId: urlProjectId,
  });

  const connectivity = createConnectivityService({
    heartbeatIntervalMs: 30000,
    heartbeatTimeoutMs: 5000,
    maxConsecutiveFailures: 3,
    performHeartbeat: async () => {
      try {
        const response = await fetch("/api/health", {
          method: "GET",
          signal: AbortSignal.timeout(5000),
        });
        return response.ok;
      } catch {
        return false;
      }
    },
  });

  const memory = createMemoryService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    projectId: urlProjectId,
    organizationId: currentOrgId,
  });

  const devices = createDevicesService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
  });

  const skills = createSkillsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    projectId: urlProjectId,
  });

  const liveSessions = createLiveSessionsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    projectId: currentProjectId,
  });

  const tokens = createTokensService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
  });

  const actions = createActionsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
  });

  const artifacts = createArtifactsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    projectId: urlProjectId,
  });

  const recentChats = createRecentChatsService({
    authReady: () => auth.isReady(),
    authToken: () => auth.token(),
    authUserId: auth.userId,
    projectId: urlProjectId,
    listRecentChats: async (pageSize, token) => {
      const res = await useChatClient().listRecentChats(
        createListRecentChatsRequest({ pageSize, cursor: "" }),
        useCallOptions(withBearerToken(token))
      );
      return res.isErr() ? [] : res.value.chats;
    },
    prefetchChats: (chatIds) => chat.prefetchChats(chatIds),
  });

  const wire: Wire = {
    wireInitialized: true as const,
    auth,
    grants,
    stream: streamingService,
    chat,
    chatTitle,
    connectivity,
    billing,
    projectBilling,
    notifications,
    ui,
    projects,
    user,
    tenants,
    activeContext,
    currentOrgId,
    homeOrgId,
    currentProjectId,
    primaryOrganizationId: () => primaryTenant()?.organizationId,
    currentTenant,
    userContext,
    labels,
    storedPrompts,
    navigation,
    views,
    assets,
    concepts,
    rels,
    memory,
    devices,
    liveSessions,
    skills,
    tokens,
    artifacts,
    suggestions,
    actions,
    featureGate,
    knowledgeUi,
    device,
    logger,
    locale,
    recentChats,
  };

  // The wire is a Solid context, so devtools has no other way to reach live
  // store state — e.g. Object.keys(stWire.stream.state.chats) to see which
  // chats are still resident.
  if (import.meta.env.DEV && !isServer) {
    window.stWire = wire;
  }

  return wire;
};

const newErrorMessage = (dependency: string): string => {
  return `Dependency validation for the wire failed for '${dependency}'.`;
};

export const validateWire = (wire: Wire) => {
  if (!wire.logger) {
    throw new Error(newErrorMessage("logger"));
  }
  if (!wire.auth) {
    throw new Error(newErrorMessage("auth"));
  }
  return;
};

const initLogger = (): Logger => (isServer ? new ServerLogger("_") : new ClientLogger("_"));
